import type * as React from "react";
import {memo} from "react";

import {
  getSalesmanPriceTable,
  isSalesmanPriceTableEmpty,
  isSpecialPrice,
} from "../../../redux/productDetailsSelectors";
import {PriceFormat} from "../../../../common/components/formatting/PriceFormat";
import {zero} from "../../../../utils/PriceUtils";
import DataDrivenTable from "../../../../common/components/Table/DataDrivenTable";
import classNames from "classnames";
import {type SalesmanPriceTableEntry} from "~/ProductDetails/models/productDetailsTypes";
import {useTranslation} from "react-i18next";
import type {ProductDescriptor} from "~/feature/ProductDescriptor/v1/types";

interface Props {
  className?: string;
  product: ProductDescriptor;
  selectedPackageCode: string;
}

const ZERO = zero();

const SalesmanPriceTable: React.FC<Props> = (props) => {
  const { product, className, selectedPackageCode } = props;
  const { t } = useTranslation();

  const isTableEmpty = isSalesmanPriceTableEmpty(product, selectedPackageCode);
  const priceTableData = getSalesmanPriceTable(product, selectedPackageCode);
  const isSpecial = isSpecialPrice(product, selectedPackageCode);

  if (isTableEmpty || !priceTableData) {
    return null;
  }

  const canSeeDiscount =
    priceTableData.filter((row: SalesmanPriceTableEntry, index: number) => row.rebate).length == priceTableData.length;
  const COLUMN_FLEX = canSeeDiscount
    ? [
        "0 0 25%", // qty
        "0 0 25%", // csp
        "0 0 25%", // rsp
        "0 0 25%", // rebate
      ]
    : [
        "0 0 34%", // qty
        "0 0 33%", // csp
        "0 0 33%", // rsp
      ];
  const dataTableContents = priceTableData.map((row) => {
    const rebateText = `${row.rebate} %`;

    const items = [
      <span key={"qty"}>{row.maxQty !== undefined ? `${row.minQty} - ${row.maxQty}` : `>= ${row.minQty}`}</span>,
      <>
        <PriceFormat key={"csp"} price={row.csp || ZERO} zeroText={"-"} />
      </>,
      <PriceFormat key={"rsp"} price={row.rsp || ZERO} zeroText={"-"} />,
    ];
    if (canSeeDiscount) {
      items.push(<span key={"rebate"}>{rebateText}</span>);
    }
    return items;
  });

  const headerT = (key: string) => t("addProduct.salesman.priceTable." + key);
  const dataTableHeader = [headerT("quantity"), headerT("csp"), headerT("rsp")];
  if (canSeeDiscount) {
    dataTableHeader.push(headerT("rebate"));
  }

  return (
    <div className={classNames(className, "scha__pdp-salesman__price-table-box")}>
      <DataDrivenTable
        header={dataTableHeader}
        data={dataTableContents}
        columnFlex={COLUMN_FLEX}
        theme={"separatorsTop"}
      />
    </div>
  );
};

export default memo(SalesmanPriceTable);
